import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  PropsWithChildren,
  useState,
  useRef,
} from "react";

import ChooseActionDropdown from "src/app/dropdowns/ChooseActionDropdown/ChooseActionDropdown";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import { ITask } from "src/redux/task/taskReducer";
import { mapMetaFieldTypeToTaskType } from "src/app/project/TableField/utils";
import useGetProjectTaskAutocompleteList from "src/app/Task/hooks/useGetProjectTaskAutocompleteList";
import { getProjectTasksAutocomplete } from "src/redux/task/taskActions";
import {
  SkeletonRect,
  SkeletonCircle,
} from "src/app/components/Skeleton/Skeleton";
import { usePromiseDispatch } from "src/hooks/usePromiseDispatch";
import { TimeoutId } from "src/types";

const LoadingSkeleton = () => {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
      }}
    >
      <SkeletonCircle size={24} />
      <SkeletonRect width={160} height={20} />
    </div>
  );
};

interface ChooseActionDropdownContainerProps {
  projectId: string | undefined;
  fieldType: MetaFieldType;
  isReadOnly?: boolean;
  action: any;
  setAction: Dispatch<SetStateAction<any>>;
  toRight?: boolean;
}

export default function ChooseActionDropdownContainer({
  projectId,
  fieldType,
  isReadOnly,
  action,
  setAction,
  toRight,
  children,
}: PropsWithChildren<ChooseActionDropdownContainerProps>) {
  const [isLoadingTasksAutocomplete, setIsLoadingTasksAutocomplete] =
    useState(false);
  const timeoutId = useRef<TimeoutId | null>(null);

  const projectTasksAutocomplete = useGetProjectTaskAutocompleteList(
    projectId ?? null,
  );

  const dispatch = usePromiseDispatch();

  let tasksAutocompleteList: ITask[] = [];
  if (
    (fieldType === MetaFieldType.Creator ||
      fieldType === MetaFieldType.Content ||
      fieldType === MetaFieldType.PublicationRelated) &&
    projectId
  ) {
    tasksAutocompleteList = (projectTasksAutocomplete ?? []).filter(
      (item) => item.taskType === mapMetaFieldTypeToTaskType[fieldType],
    );
  }

  useEffect(() => {
    if (projectTasksAutocomplete || !projectId || timeoutId.current) return;

    setIsLoadingTasksAutocomplete(true);
    timeoutId.current = setTimeout(() => {
      dispatch(getProjectTasksAutocomplete(projectId)).finally(() => {
        setIsLoadingTasksAutocomplete(false);
        timeoutId.current = null;
      });
    }, 1000);

    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = null;
      }
      setIsLoadingTasksAutocomplete(false);
    };
  }, [projectTasksAutocomplete, projectId, dispatch]);

  return (
    <ChooseActionDropdown
      value={action}
      data={tasksAutocompleteList}
      chooseAction={setAction}
      fieldType={fieldType}
      disabled={isReadOnly}
      toRight={toRight}
    >
      {isLoadingTasksAutocomplete ? <LoadingSkeleton /> : children}
    </ChooseActionDropdown>
  );
}
